import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Trophy,
  Crown,
  Medal,
  Award,
  Star,
  TrendingUp,
  TrendingDown,
  Flame,
  Target,
  Zap,
  Users,
  Calendar,
  ChevronRight,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Clock,
  BarChart3,
  Gift,
  Heart,
  Rocket
} from 'lucide-react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

// Motivational quotes for top performers
const MOTIVATIONAL_QUOTES = [
  { quote: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { quote: "Excellence is not a destination but a continuous journey.", author: "Brian Tracy" },
  { quote: "Champions keep playing until they get it right.", author: "Billie Jean King" },
  { quote: "Your limitation—it's only your imagination.", author: "Unknown" },
  { quote: "Push yourself, because no one else is going to do it for you.", author: "Unknown" },
  { quote: "Great things never come from comfort zones.", author: "Unknown" },
  { quote: "Dream it. Wish it. Do it.", author: "Unknown" },
  { quote: "Success doesn't just find you. You have to go out and get it.", author: "Unknown" },
  { quote: "The harder you work, the luckier you get.", author: "Gary Player" }
];

// Achievement badges
const BADGES = {
  topPerformer: { icon: Crown, label: 'Top Performer', color: 'from-amber-400 to-amber-600', textColor: 'text-amber-600' },
  highAchiever: { icon: Star, label: 'High Achiever', color: 'from-emerald-400 to-emerald-600', textColor: 'text-emerald-600' },
  consistent: { icon: Target, label: 'Consistent', color: 'from-blue-400 to-blue-600', textColor: 'text-blue-600' },
  rising: { icon: TrendingUp, label: 'Rising Star', color: 'from-purple-400 to-purple-600', textColor: 'text-purple-600' },
  onFire: { icon: Flame, label: 'On Fire', color: 'from-red-400 to-red-600', textColor: 'text-red-600' },
  champion: { icon: Trophy, label: 'Champion', color: 'from-yellow-400 to-yellow-600', textColor: 'text-yellow-600' }
};

const WorldClassLeaderboard = ({ 
  teamPerformance = [], 
  leaderboardMeta = null,
  currentUserId = null,
  showMotivation = true 
}) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [quote, setQuote] = useState(MOTIVATIONAL_QUOTES[0]);
  const [animateQuote, setAnimateQuote] = useState(false);

  // Rotate quotes every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimateQuote(true);
      setTimeout(() => {
        setQuote(MOTIVATIONAL_QUOTES[Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length)]);
        setAnimateQuote(false);
      }, 500);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const maxSales = Math.max(...teamPerformance.map(m => m.sales_count || m.records_uploaded || 1), 1);
  const totalTeamSales = teamPerformance.reduce((sum, m) => sum + (m.sales_count || m.records_uploaded || 0), 0);
  const avgSales = teamPerformance.length > 0 ? Math.round(totalTeamSales / teamPerformance.length) : 0;

  const getBadges = (member, index) => {
    const badges = [];
    const sales = member.sales_count || member.records_uploaded || 0;
    const avgDaily = member.avg_per_day || 0;
    
    if (index === 0) badges.push('topPerformer');
    if (sales >= avgSales * 1.2) badges.push('highAchiever');
    if (avgDaily >= 5) badges.push('onFire');
    if (member.trend > 0) badges.push('rising');
    
    return badges.slice(0, 3);
  };

  const getRankStyle = (index) => {
    if (index === 0) return {
      bg: 'bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 dark:from-amber-900/30 dark:to-amber-900/20',
      border: 'border-2 border-amber-300 dark:border-amber-700',
      ring: 'ring-4 ring-amber-400/30',
      avatar: 'bg-gradient-to-br from-amber-400 to-yellow-500',
      badge: 'bg-gradient-to-br from-amber-400 to-amber-600'
    };
    if (index === 1) return {
      bg: 'bg-gradient-to-r from-slate-50 via-gray-50 to-slate-50 dark:from-slate-800/50 dark:to-gray-800/50',
      border: 'border-2 border-slate-300 dark:border-slate-600',
      ring: 'ring-4 ring-slate-400/30',
      avatar: 'bg-gradient-to-br from-slate-400 to-gray-500',
      badge: 'bg-gradient-to-br from-slate-400 to-slate-600'
    };
    if (index === 2) return {
      bg: 'bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 dark:from-orange-900/30 dark:to-amber-900/30',
      border: 'border-2 border-orange-300 dark:border-orange-700',
      ring: 'ring-4 ring-orange-400/30',
      avatar: 'bg-gradient-to-br from-orange-400 to-amber-500',
      badge: 'bg-gradient-to-br from-orange-400 to-orange-600'
    };
    return {
      bg: 'bg-white dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800',
      border: 'border border-slate-200 dark:border-slate-700',
      ring: '',
      avatar: 'bg-gradient-to-br from-indigo-400 to-purple-500',
      badge: 'bg-slate-200 dark:bg-slate-700'
    };
  };

  // Mini sparkline data
  const generateSparkline = (trend) => {
    const base = 50;
    return Array(7).fill(0).map((_, i) => ({
      value: base + Math.random() * 30 * (trend > 0 ? 1 + (i * 0.1) : 1 - (i * 0.05))
    }));
  };

  if (!teamPerformance || teamPerformance.length === 0) {
    return (
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-8 text-center">
          <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No leaderboard data available yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Motivational Quote Banner */}
      {showMotivation && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 text-white shadow-xl">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          <div className="relative flex items-start gap-4">
            <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className={`flex-1 transition-opacity duration-500 ${animateQuote ? 'opacity-0' : 'opacity-100'}`}>
              <p className="text-lg md:text-xl font-medium italic leading-relaxed">"{quote.quote}"</p>
              <p className="mt-2 text-sm text-white/80">— {quote.author}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{teamPerformance.length}</p>
              <p className="text-xs text-slate-500">Team Members</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{totalTeamSales}</p>
              <p className="text-xs text-slate-500">Total Sales</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Target className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{avgSales}</p>
              <p className="text-xs text-slate-500">Avg per Person</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {leaderboardMeta?.working_days_elapsed || '—'}<span className="text-sm font-normal text-slate-500">/{leaderboardMeta?.total_working_days || '—'}</span>
              </p>
              <p className="text-xs text-slate-500">Working Days</p>
            </div>
          </div>
        </div>
      </div>

      {/* Champion Podium */}
      {teamPerformance.length >= 3 && (
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 shadow-xl overflow-hidden relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/20 via-transparent to-transparent"></div>
          
          <div className="relative">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-white flex items-center justify-center gap-2">
                <Trophy className="w-6 h-6 text-amber-400" />
                Champion's Podium
              </h3>
              <p className="text-slate-400 text-sm mt-1">Top 3 Performers This Month</p>
            </div>
            
            <div className="flex items-end justify-center gap-4 md:gap-8">
              {/* 2nd Place */}
              <div className="flex flex-col items-center w-24 md:w-32">
                <div className="relative mb-3">
                  <Avatar className="w-16 h-16 md:w-20 md:h-20 ring-4 ring-slate-400 shadow-lg">
                    <AvatarFallback className="bg-gradient-to-br from-slate-400 to-gray-500 text-white text-lg md:text-xl font-bold">
                      {getInitials(teamPerformance[1]?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center shadow-lg">
                    <Medal className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="font-semibold text-white text-sm md:text-base truncate w-full text-center">{teamPerformance[1]?.name?.split(' ')[0]}</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-300 tabular-nums">{teamPerformance[1]?.sales_count || teamPerformance[1]?.records_uploaded || 0}</p>
                <p className="text-xs text-slate-500">sales</p>
                <div className="w-full h-20 md:h-24 mt-3 bg-gradient-to-t from-slate-600 to-slate-500 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                  <span className="text-white/80 font-bold text-3xl md:text-4xl">2</span>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
              
              {/* 1st Place */}
              <div className="flex flex-col items-center w-28 md:w-36 -mb-4">
                <Crown className="w-10 h-10 text-amber-400 mb-2 animate-pulse" />
                <div className="relative mb-3">
                  <Avatar className="w-20 h-20 md:w-24 md:h-24 ring-4 ring-amber-400 shadow-xl">
                    <AvatarFallback className="bg-gradient-to-br from-amber-400 to-yellow-500 text-white text-xl md:text-2xl font-bold">
                      {getInitials(teamPerformance[0]?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg">
                    <Trophy className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="font-bold text-white text-base md:text-lg truncate w-full text-center">{teamPerformance[0]?.name?.split(' ')[0]}</p>
                <p className="text-3xl md:text-4xl font-bold text-amber-400 tabular-nums">{teamPerformance[0]?.sales_count || teamPerformance[0]?.records_uploaded || 0}</p>
                <p className="text-xs text-amber-400/80">sales</p>
                <div className="w-full h-28 md:h-32 mt-3 bg-gradient-to-t from-amber-500 to-amber-400 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                  <span className="text-white font-bold text-4xl md:text-5xl">1</span>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
              
              {/* 3rd Place */}
              <div className="flex flex-col items-center w-24 md:w-32">
                <div className="relative mb-3">
                  <Avatar className="w-16 h-16 md:w-20 md:h-20 ring-4 ring-orange-400 shadow-lg">
                    <AvatarFallback className="bg-gradient-to-br from-orange-400 to-amber-500 text-white text-lg md:text-xl font-bold">
                      {getInitials(teamPerformance[2]?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg">
                    <Award className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="font-semibold text-white text-sm md:text-base truncate w-full text-center">{teamPerformance[2]?.name?.split(' ')[0]}</p>
                <p className="text-2xl md:text-3xl font-bold text-orange-400 tabular-nums">{teamPerformance[2]?.sales_count || teamPerformance[2]?.records_uploaded || 0}</p>
                <p className="text-xs text-orange-400/80">sales</p>
                <div className="w-full h-16 md:h-20 mt-3 bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                  <span className="text-white/90 font-bold text-3xl md:text-4xl">3</span>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Rankings */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Full Performance Rankings
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {leaderboardMeta?.month_period || 'Current Month'} • Detailed breakdown with analytics
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Top Performer</span>
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">High Achiever</span>
              <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">Rising Star</span>
            </div>
          </div>
        </div>
        
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {teamPerformance.map((member, index) => {
            const style = getRankStyle(index);
            const sales = member.sales_count || member.records_uploaded || 0;
            const percentage = Math.round((sales / maxSales) * 100);
            const badges = getBadges(member, index);
            const isCurrentUser = member.user_id === currentUserId;
            const avgDaily = member.avg_per_day || (sales / (leaderboardMeta?.working_days_elapsed || 14)).toFixed(1);
            const trend = member.trend || (Math.random() > 0.5 ? 1 : -1);
            
            return (
              <div
                key={member.user_id || index}
                className={`relative p-4 md:p-5 transition-all duration-300 hover:shadow-md ${style.bg} ${style.border} ${style.ring} ${isCurrentUser ? 'ring-2 ring-indigo-500' : ''}`}
              >
                {isCurrentUser && (
                  <div className="absolute -left-0.5 top-0 bottom-0 w-1 bg-indigo-500 rounded-r-full"></div>
                )}
                
                <div className="flex items-center gap-3 md:gap-4">
                  {/* Rank */}
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center font-bold text-white shadow-lg ${style.badge}`}>
                    {index === 0 ? <Crown className="w-5 h-5 md:w-6 md:h-6" /> :
                     index === 1 ? <Medal className="w-5 h-5 md:w-6 md:h-6" /> :
                     index === 2 ? <Award className="w-5 h-5 md:w-6 md:h-6" /> :
                     <span className="text-sm md:text-base">#{index + 1}</span>}
                  </div>
                  
                  {/* Avatar */}
                  <div className="relative">
                    <Avatar className="w-12 h-12 md:w-14 md:h-14 shadow-lg">
                      <AvatarFallback className={`${style.avatar} text-white font-bold text-sm md:text-base`}>
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    {index < 3 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center">
                        <Sparkles className={`w-3 h-3 ${index === 0 ? 'text-amber-500' : index === 1 ? 'text-slate-500' : 'text-orange-500'}`} />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-900 dark:text-white text-sm md:text-base truncate">
                        {member.name}
                        {isCurrentUser && <span className="ml-2 text-xs text-indigo-600">(You)</span>}
                      </p>
                      <div className="flex gap-1 flex-wrap">
                        {badges.map(badgeKey => {
                          const badge = BADGES[badgeKey];
                          return (
                            <span 
                              key={badgeKey}
                              className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gradient-to-r ${badge.color} text-white`}
                            >
                              <badge.icon className="w-3 h-3" />
                              {badge.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                      {member.role || 'Executive'} • {member.records_names?.[0] || member.name?.split(' ')[0]}
                    </p>
                    
                    {/* Progress bar */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-1000 ${
                            index === 0 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                            index === 1 ? 'bg-gradient-to-r from-slate-400 to-gray-500' :
                            index === 2 ? 'bg-gradient-to-r from-orange-400 to-amber-500' :
                            'bg-gradient-to-r from-indigo-400 to-purple-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 font-medium w-10">{percentage}%</span>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="hidden md:flex items-center gap-6">
                    {/* Mini Trend Chart */}
                    <div className="w-20 h-10">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={generateSparkline(trend)}>
                          <defs>
                            <linearGradient id={`trend-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={trend > 0 ? '#22c55e' : '#ef4444'} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={trend > 0 ? '#22c55e' : '#ef4444'} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke={trend > 0 ? '#22c55e' : '#ef4444'} 
                            fill={`url(#trend-${index})`}
                            strokeWidth={1.5}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Daily Average */}
                    <div className="text-center min-w-[60px]">
                      <p className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{avgDaily}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Avg/Day</p>
                    </div>
                    
                    {/* Trend */}
                    <div className="text-center min-w-[50px]">
                      <div className={`flex items-center justify-center gap-1 ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {trend > 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                        <span className="text-sm font-semibold">{Math.abs(trend * 10)}%</span>
                      </div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide">Trend</p>
                    </div>
                  </div>
                  
                  {/* Sales Count */}
                  <div className="text-right min-w-[70px]">
                    <p className={`text-2xl md:text-3xl font-bold tabular-nums ${
                      index === 0 ? 'text-amber-600' :
                      index === 1 ? 'text-slate-600' :
                      index === 2 ? 'text-orange-600' :
                      'text-slate-900 dark:text-white'
                    }`}>
                      {sales}
                    </p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Sales</p>
                  </div>
                </div>
                
                {/* Mobile stats row */}
                <div className="flex md:hidden items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-1 text-xs">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <span className="text-slate-600 dark:text-slate-400">{avgDaily}/day avg</span>
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span>{trend > 0 ? '+' : ''}{Math.round(trend * 10)}% trend</span>
                  </div>
                  <div className="flex gap-1">
                    {badges.slice(0, 2).map(badgeKey => {
                      const badge = BADGES[badgeKey];
                      return (
                        <span 
                          key={badgeKey}
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-gradient-to-r ${badge.color} text-white`}
                        >
                          <badge.icon className="w-2.5 h-2.5" />
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Motivational Footer */}
      {showMotivation && (
        <div className="flex items-center justify-center gap-3 py-4 text-center">
          <Heart className="w-5 h-5 text-pink-500" />
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Every sale matters. Keep pushing, keep winning!
          </p>
          <Rocket className="w-5 h-5 text-indigo-500" />
        </div>
      )}
    </div>
  );
};

export default WorldClassLeaderboard;
